import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResearchSummary } from '../../types/bom.types';
import { ComponentListItem } from './discoveryModel';
import { doneTallyOf } from './researchProgress';
import ResearchProgressPopup from './ResearchProgressPopup';
import ResearchProgressStrip from './ResearchProgressStrip';
import { useResearchArrival } from '../../hooks/useResearchArrival';
import { useResearchEmailPref } from '../../hooks/useResearchEmailPref';
import { useAuth } from '../../contexts/AuthContext';

interface DiscoveryResearchStatusProps {
  bomId?: string;
  rfqId?: string;
  emailPromise?: boolean;
  summary: ResearchSummary | null;
  items: ComponentListItem[];
  /** Absent for a viewer. */
  onRetryComponent?: (key: string) => Promise<void>;
}

/**
 * The research wait above the discovery board: a summary pop-up on arrival,
 * then a strip that resolves into the final tally. Renders nothing for a BOM
 * the server sent no research summary for.
 */
export const DiscoveryResearchStatus: React.FC<DiscoveryResearchStatusProps> = ({
  bomId,
  rfqId,
  emailPromise,
  summary,
  items,
  onRetryComponent,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const arrival = useResearchArrival(bomId, summary);
  const emailPref = useResearchEmailPref(rfqId, emailPromise);
  const [retryingFailed, setRetryingFailed] = useState(false);

  if (!summary || summary.total === 0) return null;

  const retrying = items.filter((i) => i.researchState === 'RETRYING').length;
  const tally = doneTallyOf(items);

  const retryFailed = onRetryComponent
    ? async () => {
        setRetryingFailed(true);
        try {
          // One at a time: each retry restarts the poll, and the parent reports its own failures.
          for (const item of items.filter((i) => i.researchState === 'FAILED' || i.phase === 'failed')) {
            await onRetryComponent(item.key);
          }
        } finally {
          setRetryingFailed(false);
        }
      }
    : undefined;

  return (
    <>
      <ResearchProgressStrip
        summary={summary}
        retrying={retrying}
        tally={tally}
        onDetails={arrival.reopen}
        onRetryFailed={retryFailed}
        retryingFailed={retryingFailed}
      />
      <ResearchProgressPopup
        open={arrival.open}
        summary={summary}
        emailPromise={emailPref.emailPromise}
        email={currentUser?.email}
        onDontEmail={emailPref.optOut}
        dontEmailBusy={emailPref.saving}
        dontEmailFailed={emailPref.error}
        onClose={arrival.close}
        onGoToDashboard={() => navigate('/dashboard')}
      />
    </>
  );
};

export default DiscoveryResearchStatus;
